import { FunctionComponent, useEffect, useState } from 'react';
import moment from 'moment';
import { FormControl, FormHelperText, OutlinedInput, InputLabel, Typography, InputAdornment, FormControlLabel, Checkbox } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { DialogWrapper } from '../../presentation-components/display-elements-components/dialog-wrapper';
import { Moment } from 'moment';
import { IPostEnergySupplierTariffDetails } from '../../../shared/services/energy-supplier-tariff-details/energy-supplier-tariff-details.model';
import EnergySupplierTariffDetailsService from '../../../shared/services/energy-supplier-tariff-details/energy-supplier-tariff-details.service';

export const EnergySupplierTariffDetailsDialog: FunctionComponent<{
  energySupplierTariffId: string,
  open: boolean,
  closeHandler: () => void,
  isEdit: boolean,
  energySupplierTariffDetailsId?: string,
  isSubmitting: boolean,
  onSubmit: (values: IPostEnergySupplierTariffDetails, resetForm: () => void, isEdit: boolean, energySupplierId?: string) => void,
  errorMessage?: string,
  errorCode?: number,
}> = ({
  energySupplierTariffId,
  open,
  closeHandler,
  isEdit,
  energySupplierTariffDetailsId,
  isSubmitting,
  onSubmit,
  errorMessage,
  errorCode
}) => {
    //Service
    const energySupplierTariffDetailsService = new EnergySupplierTariffDetailsService()
    //State
    const [openStartDate, setOpenStartDate] = useState(false)
    const [openEndDate, setOpenEndDate] = useState(false)

    const energySupplierTariffDetailsSchema = Yup.object().shape({
      energySupplierTariffId: Yup.string().required(`You must provide an energy supplier tariff for the tariff`),
      startDate: Yup.string().required(`You must provide a start date for the tariff`),
      endDate: Yup.string().required(`You must provide an end date for the tariff`),
      multiplier: Yup.number().required(`You must provide a multiplier for the tariff`),
      source: Yup.string().required(`You must provide a source for the tariff`),
      isRenewable: Yup.boolean().required(`You must provide a renewable status for the tariff`),
      renewablePercentage: Yup.number().when('isRenewable', {
        is: true,
        then: (schema) => schema
          .required(`You must provide a renewable percentage for the tariff`)
          .min(0, 'Renewable percentage must be at least 0')
          .max(100, 'Renewable percentage cannot exceed 100'),
        otherwise: (schema) => schema.notRequired().nullable(),
      }),
    })
    const formik = useFormik<IPostEnergySupplierTariffDetails>({
      validationSchema: energySupplierTariffDetailsSchema,
      enableReinitialize: false,
      initialValues: {
        energySupplierTariffId: energySupplierTariffId,
        calendarYear: '',
        startDate: '',
        endDate: '',
        multiplier: undefined,
        source: '',
        isRenewable: false,
        renewablePercentage: undefined,
      },
      onSubmit: (values, { resetForm }) => {
        if (values.endDate === '') {
          values.endDate = null as any
        }
        if (values.startDate === '') {
          values.startDate = null as any
        }
        onSubmit(values, resetForm, isEdit, energySupplierTariffDetailsId)
      }
    })

    useEffect(() => {
      if (open && isEdit && energySupplierTariffDetailsId) {
        energySupplierTariffDetailsService.getOne(energySupplierTariffDetailsId)
          .then(response => {
            formik.setFieldValue('energySupplierTariffId', response.energySupplierTariffId)
            formik.setFieldValue('calendarYear', response.calendarYear)
            formik.setFieldValue('startDate', response.startDate)
            formik.setFieldValue('endDate', response.endDate)
            formik.setFieldValue('multiplier', response.multiplier)
            formik.setFieldValue('source', response.source)
            formik.setFieldValue('isRenewable', response.isRenewable)
            formik.setFieldValue('renewablePercentage', response.renewablePercentage)
          })
          .catch(() => { })
      } else if (open && !isEdit) {
        formik.resetForm()
        formik.setFieldValue('energySupplierTariffId', energySupplierTariffId)
        const currentYear = moment().year()
        const startDate = moment({ year: currentYear - 1, month: 3, day: 1 })
        const endDate = moment({ year: currentYear, month: 2, day: 31 })
        formik.setFieldValue('startDate', startDate.format('YYYY-MM-DD'))
        formik.setFieldValue('endDate', endDate.format('YYYY-MM-DD'))
      }
    }, [open, isEdit, energySupplierTariffDetailsId])

    const checkboxHandler = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
      const { checked } = e.target;
      formik.setValues({ ...formik.values, [name]: checked })
    }

    const dateHandler = (date: Moment | null, name: string) => {
      if (date) {
        formik.setFieldValue(name, date.format('YYYY-MM-DD'))
      } else {
        formik.setFieldValue(name, '')
      }
    }


    return (
      <DialogWrapper
        open={open}
        onClose={closeHandler}
        title={isEdit ? 'Edit Tariff Year' : 'Add Tariff Year'}
        cancelHandler={closeHandler}
        submitHandler={formik.submitForm}
        isSubmitting={isSubmitting}
        disableSaveButton={isSubmitting}
      >
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label='Start Date'
              value={formik.values.startDate ? moment(formik.values.startDate) : null}
              onChange={(date) => dateHandler(date, 'startDate')}
              format='DD/MM/YYYY'
              sx={{ color: 'text.primary' }}
              open={openStartDate}
              onOpen={() => setOpenStartDate(true)}
              onClose={() => setOpenStartDate(false)}
              slotProps={{
                textField: {
                  onClick: () => setOpenStartDate(true),
                  error: formik.touched.startDate && Boolean(formik.errors.startDate)
                },
              }}
            />
          </LocalizationProvider>
          {formik.touched.startDate && formik.errors.startDate &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.startDate ? <div>{formik.errors.startDate as string}</div> : null}
            </FormHelperText>
          }
          {errorCode === 1 && errorMessage &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {errorMessage}
            </FormHelperText>
          }
        </FormControl>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label='End Date'
              value={formik.values.endDate ? moment(formik.values.endDate) : null}
              onChange={(date) => dateHandler(date, 'endDate')}
              format='DD/MM/YYYY'
              sx={{ color: 'text.primary' }}
              open={openEndDate}
              onOpen={() => setOpenEndDate(true)}
              onClose={() => setOpenEndDate(false)}
              slotProps={{
                textField: {
                  onClick: () => setOpenEndDate(true),
                  error: formik.touched.endDate && Boolean(formik.errors.endDate),
                },
              }}
            />
          </LocalizationProvider>
          {formik.touched.endDate && formik.errors.endDate &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.endDate ? <div>{formik.errors.endDate as string}</div> : null}
            </FormHelperText>
          }
          {errorCode === 1 && errorMessage &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {errorMessage}
            </FormHelperText>
          }
        </FormControl>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel>Supplier Emission Rate</InputLabel>
          <OutlinedInput
            type='text'
            value={formik.values.multiplier != null ? formik.values.multiplier.toString() : ''}
            onChange={(e) => formik.setFieldValue('multiplier', e.target.value)}
            onBlur={(e) => {
              const value = e.target.value;
              if (value === '') {
                formik.setFieldValue('multiplier', '');
              } else {
                const numValue = Number(value);
                if (!isNaN(numValue)) {
                  formik.setFieldValue('multiplier', numValue);
                }
              }
              formik.handleBlur(e);
            }}
            inputProps={{ min: 0, step: 0.0001 }}
            endAdornment={<InputAdornment position="end"><Typography variant='body2' sx={{ color: 'text.secondary' }}>kgCO<sub>2</sub>/kWh</Typography></InputAdornment>}
            error={formik.touched.multiplier && Boolean(formik.errors.multiplier)}
            label='Supplier Emission Rate'
          />
          {formik.touched.multiplier && formik.errors.multiplier &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.multiplier ? <div>{String(formik.errors.multiplier)}</div> : null}
            </FormHelperText>
          }
        </FormControl>
        <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
          <InputLabel>Source</InputLabel>
          <OutlinedInput
            type='text'
            value={formik.values.source || ''}
            onChange={(e) => formik.setFieldValue('source', e.target.value)}
            error={formik.touched.source && Boolean(formik.errors.source)}
            label='Source'
          />
          {formik.touched.source && formik.errors.source &&
            <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
              {formik.errors.source ? <div>{String(formik.errors.source)}</div> : null}
            </FormHelperText>
          }
        </FormControl>
        <Typography variant='subtitle1' sx={{ mt: 2 }}> Is this a renewable tariff?</Typography>
        <FormControlLabel
          control={<Checkbox checked={formik.values.isRenewable} onChange={(e) => checkboxHandler(e as any, 'isRenewable')} />}
          label='Renewable'
          sx={{ mt: 1 }}
        />
        {formik.values.isRenewable && (
          <>
            <FormControl fullWidth error={formik.touched.renewablePercentage && Boolean(formik.errors.renewablePercentage)} sx={{ mt: 2 }}>
              <InputLabel htmlFor='renewable-percentage'>Renewable Percentage</InputLabel>
              <OutlinedInput
                id='renewable-percentage'
                name='renewablePercentage'
                type='number'
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                label='Renewable Percentage'
                value={formik.values.renewablePercentage ?? ''}
                onChange={formik.handleChange}
                endAdornment={<InputAdornment position='end'>%</InputAdornment>}
              />
              {formik.touched.renewablePercentage && formik.errors.renewablePercentage && <FormHelperText>{formik.touched.renewablePercentage && formik.errors.renewablePercentage ? String(formik.errors.renewablePercentage) : ' '} </FormHelperText>}
            </FormControl>
          </>
        )}
      </DialogWrapper>
    )
  }


